import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Collapse, Flex, IconButton, Text } from "@chakra-ui/react";
import {
  CaretDown,
  Check,
  Clock,
  Compass,
  File,
  FileCsv,
  FileDoc,
  FilePdf,
  FilePpt,
  FileText,
  FileXls,
  Sparkle,
} from "@phosphor-icons/react";
import RichTextEditor, { RichTextViewer } from "../../../ISSB/IssbDataInput/components/RichTextEditor/RichTextEditor";
import useAirUserRoles from "../../../../utils/AuthUtils";
import {
  boldRowCounts,
  extractCommittedDate,
  extractRunReference,
  extractSource,
  formatCommittedDate,
  parseMethodologySections,
  restyleTransformations,
  stripAiAssistedIngestLabel,
} from "./methodologySections";

// Matches the FILE_TYPE_ICONS convention used by AIReviewSourcesPanel.jsx / AIDashboard.jsx /
// AnswerActivityOneEvent.jsx — each of those keeps its own local copy rather than a shared util.
const FILE_TYPE_ICONS = {
  PDF: FilePdf,
  DOCX: FileDoc,
  DOC: FileDoc,
  XLSX: FileXls,
  XLS: FileXls,
  CSV: FileCsv,
  PPTX: FilePpt,
  PPT: FilePpt,
  TXT: FileText,
};

function getFileIcon(filename) {
  const ext = (filename?.split(".").pop() || "").toUpperCase();
  return FILE_TYPE_ICONS[ext] || File;
}

// One-file "Document Sources" row (AIReviewSourcesPanel.jsx) — file-type icon + filename —
// shown under the AI-ingest chip.
function SourceRow({ filename }) {
  if (!filename) return null;
  const Icon = getFileIcon(filename);
  return (
    <Flex
      align="center"
      gap="6px"
      mb="3px"
      display="inline-flex"
      maxW="100%"
      bg="pureWhite"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      px="10px"
      py="6px"
    >
      <Box as="span" color="gray.500" display="inline-flex" alignItems="center">
        <Icon size={14} />
      </Box>
      <Text fontSize="12px" color="gray.700" noOfLines={1}>
        {filename}
      </Text>
    </Flex>
  );
}

// Reusable header strip used by both cards. Eyebrow + title + sub-copy.
// When `onToggle` is provided the header doubles as an accordion control —
// the whole strip is clickable and a chevron rotates to reflect open state.
function ColHeader({ icon, eyebrow, title, sub, isOpen, onToggle, badgeCount, showTick }) {
  const isClickable = typeof onToggle === "function";
  return (
    <Flex
      align="flex-start"
      gap={3}
      mb={isOpen ? "14px" : "0"}
      onClick={isClickable ? onToggle : undefined}
      cursor={isClickable ? "pointer" : "default"}
      role={isClickable ? "button" : undefined}
      aria-expanded={isClickable ? !!isOpen : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } } : undefined}
    >
      <Box flex={1}>
        <Flex align="center" gap={2} mb={1}>
          {icon}
          <Text
            fontSize="11px"
            fontWeight="700"
            color="limeShade.600"
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            {eyebrow}
          </Text>
          {typeof badgeCount === "number" && badgeCount > 0 && (
            <Flex
              align="center"
              justify="center"
              minW="16px"
              h="16px"
              px="5px"
              borderRadius="full"
              bg="lightLime"
              color="limeShade.700"
              fontSize="10px"
              fontWeight="700"
              lineHeight="1"
            >
              {badgeCount}
            </Flex>
          )}
          {showTick && (
            <Flex
              align="center"
              justify="center"
              minW="16px"
              h="16px"
              px="5px"
              borderRadius="full"
              bg="lightLime"
              color="limeShade.700"
              aria-label="Methodology added"
            >
              <Check size={10} weight="bold" />
            </Flex>
          )}
        </Flex>
        <Text fontSize="17px" fontWeight="700" color="darkTeal" mb={1}>
          {title}
        </Text>
        <Text fontSize="12px" color="whiteShade.600" lineHeight="1.55">
          {sub}
        </Text>
      </Box>
      {isClickable && (
        <IconButton
          aria-label={isOpen ? "Collapse" : "Expand"}
          icon={
            <Box transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"} transition="transform 0.15s">
              <CaretDown size={16} />
            </Box>
          }
          variant="ghost"
          size="sm"
          color="gray.400"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          _hover={{ color: "darkTeal", bg: "transparent" }}
          tabIndex={-1}
        />
      )}
    </Flex>
  );
}

// Renders methodology HTML broken into its AI-ingest vs human-typed segments, flagging
// each AI-contributed block with a chip. One scope's box can hold more than one AI
// block (e.g. separate stationary + mobile combustion ingests both land in "scope1"),
// so the chip is per-segment rather than once for the whole card.
//
// The chip/date/source-row/bullet treatment is ESG_ADMIN-only. Everyone else still sees the
// underlying methodology text — it's not hidden, just rendered plainly, exactly as it was
// stored, the way it always rendered before this treatment existed.
function MethodologySections({ content }) {
  const { isESGAdmin } = useAirUserRoles();
  const sections = parseMethodologySections(content);
  return sections.map((section, i) => {
    if (section.type !== "ai" || !isESGAdmin) {
      return (
        <Box key={i} mt={i > 0 ? "14px" : 0}>
          <RichTextViewer content={section.html} />
        </Box>
      );
    }

    const labelStripped = stripAiAssistedIngestLabel(section.html);
    const { date: committedDate, html: afterDate } = extractCommittedDate(labelStripped);
    const { runId, html: afterRun } = extractRunReference(afterDate);
    const { filename: sourceFilename, html: bodyHtml } = extractSource(afterRun);

    return (
      <Box key={i} mt={i > 0 ? "14px" : 0}>
        <Flex align="center" gap="8px" mb="8px">
          <Flex align="center" gap="6px">
            <Sparkle size={14} weight="fill" color="var(--chakra-colors-limeShade-600)" />
            <Text
              fontSize="12px"
              fontWeight="700"
              color="limeShade.700"
              bg="lightLime"
              borderRadius="full"
              px="10px"
              py="4px"
              letterSpacing="0.02em"
            >
              AI-Assisted Ingest
            </Text>
          </Flex>
          {committedDate && (
            <Flex align="center" gap="4px" color="gray.500">
              <Clock size={14} color="#A0AEC0" />
              <Text fontSize="12px" fontWeight="600">
                {formatCommittedDate(committedDate)}
              </Text>
            </Flex>
          )}
          {runId && (
            <Text fontSize="10px" color="gray.400" fontWeight="500">
              #{runId}
            </Text>
          )}
        </Flex>
        <SourceRow filename={sourceFilename} />
        <Box sx={{ "ul li::marker": { color: "var(--chakra-colors-limeShade-600)" } }}>
          <RichTextViewer content={boldRowCounts(restyleTransformations(bodyHtml))} />
        </Box>
      </Box>
    );
  });
}

// Three states are driven from `editing` + `savedContent`:
//  - empty/editing : TipTap editor with Save (no Cancel)
//  - saved view    : RichTextViewer in gray.50 + Edit button
//  - re-editing    : TipTap editor with Save + Cancel
//  - readOnly      : RichTextViewer only (no Edit) — used for finalised tasks
function MethodologyCard({
  initialContent, onSave, isSaving, readOnly = false, isOpen, onToggle,
  // Rendered inside the collapse, after the notes. Optional so the card stays usable anywhere.
  footer = null,
  // Rendered inside the collapse, above the notes: the per-type tab row when a scope has several types
  // (Scope 1's Stationary / Mobile / Refrigerants). Each type is its own card instance, keyed by type.
  tabs = null,
  // Header tick: whether ANY type has methodology, so the tick reflects the whole scope, not one tab.
  anyContent,
}) {
  const [savedContent, setSavedContent] = useState(initialContent || "");
  const [editing, setEditing] = useState(!initialContent && !readOnly);
  const draftRef = useRef(initialContent || "");
  const [isDirty, setIsDirty] = useState(false);

  // Sync the local saved-content snapshot whenever the parent feeds in new
  // initial content (e.g. after a fresh fetch on tab switch).
  useEffect(() => {
    setSavedContent(initialContent || "");
    draftRef.current = initialContent || "";
    setEditing(!initialContent && !readOnly);
    setIsDirty(false);
  }, [initialContent, readOnly]);

  // If the user collapses the accordion while the editor is dirty without
  // saving, treat that as Cancel — discard the unsaved draft so reopening
  // shows the last saved state. We also reset back to the read view if
  // there's saved content.
  useEffect(() => {
    if (isOpen) return;
    if (!isDirty && !editing) return;
    draftRef.current = savedContent;
    setIsDirty(false);
    if (savedContent) setEditing(false);
  }, [isOpen, isDirty, editing, savedContent]);

  const handleChange = (html) => {
    draftRef.current = html;
    setIsDirty(true);
  };

  const handleSave = () => {
    const next = draftRef.current;
    setSavedContent(next);
    setEditing(false);
    setIsDirty(false);
    onSave?.(next);
  };

  const handleCancel = () => {
    draftRef.current = savedContent;
    setIsDirty(false);
    setEditing(false);
  };

  const handleEdit = () => {
    draftRef.current = savedContent;
    setEditing(true);
    setIsDirty(false);
  };

  const showReadView = !!savedContent && !editing;

  return (
    <Box
      bg="pureWhite"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="14px"
      p="22px 24px"
      h="100%"
    >
      <ColHeader
        icon={<Compass size={14} color="var(--chakra-colors-limeShade-600)" />}
        eyebrow="Methodology"
        title="How was this data collected?"
        sub="Add any assumptions, methodology and data quality notes to support how or why this data was collected."
        isOpen={isOpen}
        onToggle={onToggle}
        showTick={anyContent ?? (!!savedContent && savedContent.trim().length > 0)}
      />

      <Collapse in={isOpen} animateOpacity>
      {tabs}
      {readOnly ? (
        <Box bg="gray.50" borderRadius="10px" p="18px">
          {savedContent ? (
            <MethodologySections content={savedContent} />
          ) : (
            <Text fontSize="13.5px" color="gray.400" fontStyle="italic">
              No methodology recorded.
            </Text>
          )}
        </Box>
      ) : showReadView ? (
        <>
          <Box bg="gray.50" borderRadius="10px" p="18px">
            <MethodologySections content={savedContent} />
          </Box>
          <Flex justify="flex-end" mt="10px">
            <Button
              variant="outline"
              size="sm"
              borderColor="gray.200"
              color="darkTeal"
              fontSize="12px"
              fontWeight="600"
              borderRadius="8px"
              onClick={handleEdit}
              _hover={{ bg: "gray.50" }}
            >
              Edit
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <Box border="1px solid" borderColor="gray.100" borderRadius="10px" overflow="hidden">
            <RichTextEditor content={draftRef.current} onChange={handleChange} />
          </Box>
          <Flex align="center" justify="flex-end" mt={3}>
            <Flex gap={2}>
              {savedContent && (
                <Button
                  variant="outline"
                  size="sm"
                  borderColor="gray.200"
                  color="darkTeal"
                  fontSize="12px"
                  fontWeight="600"
                  borderRadius="8px"
                  onClick={handleCancel}
                  _hover={{ bg: "gray.50" }}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                bg={isDirty ? "lime" : "darkTeal"}
                color={isDirty ? "pureBlack" : "white"}
                fontSize="12px"
                fontWeight="600"
                borderRadius="8px"
                px="16px"
                onClick={handleSave}
                isDisabled={!isDirty}
                isLoading={isSaving}
                _hover={isDirty ? { bg: "limeShade.300" } : { bg: "darkTeal", opacity: 0.9 }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </>
      )}
      {footer}
      </Collapse>
    </Box>
  );
}

export default MethodologyCard;
export { ColHeader };
